import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MDBBadge,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBTable,
  MDBTableBody,
  MDBTableHead,
} from "mdb-react-ui-kit";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { getAllExceptions } from "Projects/CC_cubi_clan_project/Utils/IncidentService";
import "./IncidentExceptionsPanel.css";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("es-ES", { hour12: false });
}

const COLUMN_CONFIG = {
  id: { label: "#", defaultVisible: true, defaultWidth: 62, minWidth: 56 },
  date: { label: "Fecha", defaultVisible: true, defaultWidth: 148, minWidth: 130 },
  className: { label: "Clase", defaultVisible: true, defaultWidth: 260, minWidth: 170 },
  methodName: { label: "Método", defaultVisible: true, defaultWidth: 210, minWidth: 140 },
  user: { label: "Usuario", defaultVisible: true, defaultWidth: 58, minWidth: 58 },
  message: { label: "Mensaje", defaultVisible: true, defaultWidth: 340, minWidth: 170 },
  trace: { label: "Traza", defaultVisible: false, defaultWidth: 380, minWidth: 220 },
};

const DEFAULT_ORDER = ["id", "date", "className", "methodName", "user", "message", "trace"];

function getExceptionField(row, key) {
  if (key === "id") return row?.iDException ?? "-";
  if (key === "date") return formatDate(row?.date);
  if (key === "className") return row?.className || "-";
  if (key === "methodName") return row?.methodName || "-";
  if (key === "user") return row?.user ?? "-";
  if (key === "message") return row?.message || "-";
  if (key === "trace") return row?.trace || "-";
  return "-";
}

export default function IncidentExceptionsPanel() {
  const EMPTY_FILTERS = {
    appCode: "",
    className: "",
    methodName: "",
    userId: "",
    message: "",
  };

  const [loading, setLoading] = useState(false);
  const [exceptions, setExceptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedException, setSelectedException] = useState(null);
  const [showColumnsConfig, setShowColumnsConfig] = useState(false);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_ORDER);
  const [visibleColumns, setVisibleColumns] = useState(
    DEFAULT_ORDER.reduce((acc, key) => {
      acc[key] = !!COLUMN_CONFIG[key]?.defaultVisible;
      return acc;
    }, {})
  );
  const [columnWidths, setColumnWidths] = useState(
    DEFAULT_ORDER.reduce((acc, key) => {
      acc[key] = COLUMN_CONFIG[key]?.defaultWidth || 140;
      return acc;
    }, {})
  );
  const [resizingColumn, setResizingColumn] = useState(null);
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const pageCacheRef = useRef(new Map());

  const normalizedFilters = (filters) => ({
    appCode: (filters.appCode || "").trim().toUpperCase(),
    className: (filters.className || "").trim(),
    methodName: (filters.methodName || "").trim(),
    userId: (filters.userId || "").trim(),
    message: (filters.message || "").trim(),
  });

  const cacheKey = (targetPage, targetPageSize, filters) => {
    return `${targetPageSize}:${targetPage}:${JSON.stringify(normalizedFilters(filters))}`;
  };

  const applyPageResult = (result, fallbackPage) => {
    setExceptions(result.rows || []);
    setPage(result.page || fallbackPage);
    setTotalRows(result.totalRows || 0);
    setTotalPages(result.totalPages || 0);
  };

  const prefetchPage = async (targetPage) => {
    if (targetPage < 1) return;
    const key = cacheKey(targetPage, pageSize, activeFilters);
    if (pageCacheRef.current.has(key)) return;
    try {
      const nf = normalizedFilters(activeFilters);
      const result = await getAllExceptions({
        page: targetPage,
        pageSize,
        appCode: nf.appCode || undefined,
        className: nf.className || undefined,
        methodName: nf.methodName || undefined,
        userId: nf.userId || undefined,
        message: nf.message || undefined,
      });
      pageCacheRef.current.set(key, result);
    } catch {
      // Prefetch opportunista: ignorar errores silenciosamente.
    }
  };

  const prefetchNeighbors = (currentPage, knownTotalPages) => {
    if (currentPage > 1) {
      prefetchPage(currentPage - 1);
    }
    if (!knownTotalPages || currentPage < knownTotalPages) {
      prefetchPage(currentPage + 1);
    }
  };

  const visibleOrderedColumns = useMemo(() => {
    return columnOrder.filter((key) => visibleColumns[key]);
  }, [columnOrder, visibleColumns]);

  const loadExceptions = async (targetPage = page, filtersArg = activeFilters) => {
    const key = cacheKey(targetPage, pageSize, filtersArg);
    const cached = pageCacheRef.current.get(key);
    if (cached) {
      applyPageResult(cached, targetPage);
      prefetchNeighbors(cached.page || targetPage, cached.totalPages || 0);
      return;
    }

    setLoading(true);
    try {
      const nf = normalizedFilters(filtersArg);
      const result = await getAllExceptions({
        page: targetPage,
        pageSize,
        appCode: nf.appCode || undefined,
        className: nf.className || undefined,
        methodName: nf.methodName || undefined,
        userId: nf.userId || undefined,
        message: nf.message || undefined,
      });
      pageCacheRef.current.set(key, result);
      applyPageResult(result, targetPage);
      prefetchNeighbors(result.page || targetPage, result.totalPages || 0);
    } catch (error) {
      toast.error(error?.message || "No se pudieron cargar las exceptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!resizingColumn) return undefined;

    const onMouseMove = (event) => {
      const nextWidth = resizingColumn.startWidth + (event.clientX - resizingColumn.startX);
      const minWidth = COLUMN_CONFIG[resizingColumn.key]?.minWidth || 70;
      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn.key]: Math.max(minWidth, nextWidth),
      }));
    };

    const onMouseUp = () => setResizingColumn(null);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizingColumn]);

  const goToPage = (nextPage) => {
    if (nextPage < 1) return;
    if (totalPages > 0 && nextPage > totalPages) return;
    loadExceptions(nextPage);
  };

  const onPageSizeChange = (event) => {
    const value = Number(event.target.value);
    const safeValue = Number.isFinite(value) ? value : 50;
    setPageSize(safeValue);
    setPage(1);
  };

  useEffect(() => {
    pageCacheRef.current.clear();
    loadExceptions(1, activeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, activeFilters]);

  const applyFilters = () => {
    const nextFilters = normalizedFilters(draftFilters);
    pageCacheRef.current.clear();
    setActiveFilters(nextFilters);
    setPage(1);
  };

  const resetFilters = () => {
    pageCacheRef.current.clear();
    setDraftFilters(EMPTY_FILTERS);
    setActiveFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const toggleColumnVisibility = (key) => {
    const currentlyVisible = !!visibleColumns[key];
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;
    if (currentlyVisible && visibleCount <= 1) {
      return;
    }
    setVisibleColumns((prev) => ({ ...prev, [key]: !currentlyVisible }));
  };

  const moveColumn = (key, direction) => {
    const index = columnOrder.indexOf(key);
    if (index === -1) return;
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= columnOrder.length) return;

    const nextOrder = [...columnOrder];
    [nextOrder[index], nextOrder[targetIndex]] = [nextOrder[targetIndex], nextOrder[index]];
    setColumnOrder(nextOrder);
  };

  const startResize = (event, key) => {
    event.preventDefault();
    setResizingColumn({
      key,
      startX: event.clientX,
      startWidth: columnWidths[key] || COLUMN_CONFIG[key]?.defaultWidth || 120,
    });
  };

  const renderPager = () => (
    <div className="d-flex justify-content-between align-items-center mt-2 mb-2 inci-exc-pagination flex-wrap gap-2">
      <div className="d-flex align-items-center gap-2">
        <label htmlFor="exc-page-size" className="text-muted mb-0">Filas/página</label>
        <select
          id="exc-page-size"
          className="form-select form-select-sm"
          value={pageSize}
          onChange={onPageSizeChange}
          style={{ width: "90px" }}
          disabled={loading}
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
      </div>

      <div className="text-muted">
        {totalRows > 0
          ? `Mostrando ${exceptions.length} de ${totalRows} exceptions (página ${page}${totalPages ? `/${totalPages}` : ""})`
          : "Sin datos"}
      </div>

      <div className="d-flex gap-2">
        <MDBBtn size="sm" color="light" onClick={() => goToPage(page - 1)} disabled={loading || page <= 1}>
          Anterior
        </MDBBtn>
        <MDBBtn
          size="sm"
          color="light"
          onClick={() => goToPage(page + 1)}
          disabled={loading || (totalPages > 0 && page >= totalPages)}
        >
          Siguiente
        </MDBBtn>
      </div>
    </div>
  );

  return (
    <MDBCard className="inci-exc-panel">
      <MDBCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Exceptions recientes</h5>
          <div className="text-muted small">Listado técnico paginado y ordenado por fecha descendente.</div>
        </div>
        <div className="d-flex gap-2">
          <MDBBtn size="sm" color="light" onClick={() => setShowColumnsConfig((prev) => !prev)}>
            <MDBIcon fas icon="table-columns" className="me-2" />Columnas
          </MDBBtn>
          <MDBBtn size="sm" color="secondary" outline onClick={() => loadExceptions(page)} disabled={loading}>
            <MDBIcon fas icon="rotate" className="me-2" />Actualizar
          </MDBBtn>
        </div>
      </MDBCardHeader>

      <MDBCardBody>
        <div className="inci-exc-filters mb-3">
          <div className="d-flex flex-wrap gap-2">
            <select
              className="form-select form-select-sm"
              value={draftFilters.appCode}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, appCode: event.target.value }))}
            >
              <option value="">Aplicación: todas</option>
              <option value="SSO">SSO</option>
              <option value="TEAN">TEAN</option>
              <option value="CCON">CCON</option>
              <option value="TABL">TABL</option>
              <option value="HAPK">HAPK</option>
              <option value="BOX">BOX</option>
              <option value="GMF">GMF</option>
              <option value="MFM">MFM</option>
              <option value="CUBE">CUBE</option>
            </select>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Clase del error"
              value={draftFilters.className}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, className: event.target.value }))}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Método del error"
              value={draftFilters.methodName}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, methodName: event.target.value }))}
            />
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Usuario error (ID)"
              value={draftFilters.userId}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, userId: event.target.value }))}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Mensaje contiene"
              value={draftFilters.message}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, message: event.target.value }))}
            />
          </div>
          <div className="d-flex gap-2 mt-2">
            <MDBBtn size="sm" color="primary" onClick={applyFilters} disabled={loading}>Filtrar</MDBBtn>
            <MDBBtn size="sm" color="light" onClick={resetFilters} disabled={loading}>Limpiar</MDBBtn>
          </div>
        </div>

        {showColumnsConfig && (
          <div className="inci-exc-columns mb-3">
            <div className="fw-semibold mb-2">Columnas visibles y orden</div>
            {columnOrder.map((key, index) => (
              <div key={key} className="inci-exc-columns-row">
                <label className="d-flex align-items-center gap-2 m-0">
                  <input
                    type="checkbox"
                    checked={!!visibleColumns[key]}
                    onChange={() => toggleColumnVisibility(key)}
                  />
                  <span>{COLUMN_CONFIG[key].label}</span>
                </label>
                <div className="d-flex gap-1">
                  <MDBBtn size="sm" color="light" disabled={index === 0} onClick={() => moveColumn(key, "up")}>
                    <MDBIcon fas icon="arrow-up" />
                  </MDBBtn>
                  <MDBBtn
                    size="sm"
                    color="light"
                    disabled={index === columnOrder.length - 1}
                    onClick={() => moveColumn(key, "down")}
                  >
                    <MDBIcon fas icon="arrow-down" />
                  </MDBBtn>
                </div>
              </div>
            ))}
            <div className="text-muted small mt-2">
              Puedes reordenar con flechas y hacer resize arrastrando el borde derecho de cada cabecera.
            </div>
          </div>
        )}

        {!loading && !exceptions.length && (
          <div className="text-muted">No hay exceptions registradas para mostrar.</div>
        )}

        {!!exceptions.length && (
          <>
            {renderPager()}
            <MDBTable striped hover responsive small className="inci-exc-table">
              <colgroup>
                {visibleOrderedColumns.map((key) => (
                  <col key={`col-${key}`} style={{ width: `${columnWidths[key]}px` }} />
                ))}
                <col style={{ width: "86px" }} />
              </colgroup>
              <MDBTableHead>
                <tr>
                  {visibleOrderedColumns.map((key) => (
                    <th key={`head-${key}`} className={`col-${key}`}>
                      <div className="inci-exc-head-wrap">
                        <span>{COLUMN_CONFIG[key].label}</span>
                        <span className="inci-exc-resizer" onMouseDown={(event) => startResize(event, key)} />
                      </div>
                    </th>
                  ))}
                  <th className="col-actions">Detalle</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {exceptions.map((row) => (
                  <tr key={row.iDException || `${row.date}-${row.className}-${row.methodName}`}>
                    {visibleOrderedColumns.map((key) => {
                      const value = getExceptionField(row, key);
                      if (key === "id") {
                        return (
                          <td key={`cell-${row.iDException}-${key}`} className="col-id">
                            <MDBBadge color="dark">{value}</MDBBadge>
                          </td>
                        );
                      }
                      return (
                        <td key={`cell-${row.iDException}-${key}`} className={`col-${key} inci-exc-cell`} title={String(value)}>
                          {value}
                        </td>
                      );
                    })}
                    <td className="col-actions">
                      <MDBBtn size="sm" color="dark" outline onClick={() => setSelectedException(row)}>
                        Ver
                      </MDBBtn>
                    </td>
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          </>
        )}

        {!!exceptions.length && renderPager()}
      </MDBCardBody>

      <MDBModal tabIndex="-1" open={!!selectedException} setOpen={() => setSelectedException(null)}>
        <MDBModalDialog size="xl" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                Exception #{selectedException?.iDException ?? "-"}
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setSelectedException(null)} />
            </MDBModalHeader>
            <MDBModalBody>
              <div className="inci-exc-detail-grid">
                <div><strong>Fecha:</strong> {formatDate(selectedException?.date)}</div>
                <div><strong>Usuario:</strong> {selectedException?.user ?? "-"}</div>
                <div className="text-break"><strong>Clase:</strong> {selectedException?.className || "-"}</div>
                <div className="text-break"><strong>Método:</strong> {selectedException?.methodName || "-"}</div>
                <div className="text-break"><strong>Mensaje:</strong> {selectedException?.message || "-"}</div>
              </div>

              <hr />
              <div className="fw-semibold mb-2">Traza completa</div>
              <pre className="inci-exc-trace">{selectedException?.trace || "Sin traza"}</pre>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={() => setSelectedException(null)}>Cerrar</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </MDBCard>
  );
}
